import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  FolderTree,
  Server,
  Layers,
  GitBranch,
  Rocket,
} from "lucide-react";
import { groupsApi, Group } from "@/api/client";
import { formatDate } from "@/lib/utils";

export default function Groups() {
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: groupsApi.list,
  });

  const createMutation = useMutation({
    mutationKey: ["createGroup"],
    mutationFn: groupsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateGroup"],
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof groupsApi.update>[1]) =>
      groupsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setEditingGroup(null);
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteGroup"],
    mutationFn: groupsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const enableSimpleReleases =
      formData.get("enable_simple_releases") === "on";
    const enableReleaseCycles = formData.get("enable_release_cycles") === "on";

    // Validate: at least one must be enabled
    if (!enableSimpleReleases && !enableReleaseCycles) {
      alert(
        "At least one workflow option must be enabled (Simple Releases or Release Cycles)",
      );
      return;
    }

    const groupData = {
      name: formData.get("name") as string,
      slug: (formData.get("slug") as string) || undefined,
      description: formData.get("description") as string,
      enable_simple_releases: enableSimpleReleases,
      enable_release_cycles: enableReleaseCycles,
    };

    if (editingGroup) {
      updateMutation.mutate({ id: editingGroup.id, ...groupData });
    } else {
      createMutation.mutate(groupData);
    }
  };

  const handleDelete = (id: string, name: string) => {
    if (
      confirm(
        `Are you sure you want to delete "${name}"? Environments and services in this group will become ungrouped.`,
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Groups</h1>
          <p className="mt-1 text-sm text-gray-500">
            Organize environments and services by customer, product, or region
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingGroup(null);
          }}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Group
        </button>
      </div>

      {/* Form */}
      {(showForm || editingGroup) && (
        <div className="mt-6 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingGroup ? "Edit Group" : "Add Group"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="label">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  className="input"
                  placeholder="Customer A"
                  defaultValue={editingGroup?.name}
                  required
                />
              </div>
              <div>
                <label htmlFor="slug" className="label">
                  Slug (optional)
                </label>
                <input
                  type="text"
                  name="slug"
                  id="slug"
                  className="input"
                  placeholder="customer-a"
                  defaultValue={editingGroup?.slug}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Auto-generated from name if empty
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="label">
                Description (optional)
              </label>
              <textarea
                name="description"
                id="description"
                rows={2}
                className="input"
                placeholder="All environments and services for Customer A"
                defaultValue={editingGroup?.description}
              />
            </div>

            {/* Workflow Settings */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Workflow Settings
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Control which deployment workflows are available for services in
                this group. At least one option must be enabled.
              </p>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="enable_simple_releases"
                    defaultChecked={
                      editingGroup?.enable_simple_releases ?? true
                    }
                    className="h-4 w-4 mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Rocket className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Simple Releases
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Quick deployments without release cycle tracking. Ideal
                      for internal tools or rapid iteration.
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="enable_release_cycles"
                    defaultChecked={editingGroup?.enable_release_cycles ?? true}
                    className="h-4 w-4 mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <GitBranch className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Release Cycles
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Coordinated multi-service releases with customer versions.
                      Required for enterprise/compliance workflows.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingGroup(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : "Save Group"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Groups List */}
      <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <div className="text-gray-500">Loading...</div>
        ) : (data?.groups ?? []).length === 0 ? (
          <div className="card text-center py-8 col-span-full">
            <FolderTree className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              No groups yet. Create groups to organize your environments and
              services.
            </p>
          </div>
        ) : (
          (data?.groups ?? []).map((group) => (
            <Link
              key={group.id}
              to={`/groups/${group.slug}`}
              className="card hover:shadow-md transition-shadow block"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <FolderTree className="h-5 w-5 text-brand-500" />
                  <div>
                    <h3 className="font-medium text-gray-900">{group.name}</h3>
                    <p className="text-sm text-gray-500">{group.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingGroup(group);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDelete(group.id, group.name);
                    }}
                    className="p-2 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {group.description && (
                <p className="mt-3 text-sm text-gray-600">
                  {group.description}
                </p>
              )}

              <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Layers className="h-4 w-4" />
                  <span>
                    {group.environment_count ?? 0} environment
                    {group.environment_count !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Server className="h-4 w-4" />
                  <span>
                    {group.service_count ?? 0} service
                    {group.service_count !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              {/* Workflow badges */}
              <div className="mt-3 flex items-center gap-2">
                {group.enable_simple_releases && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                    <Rocket className="h-3 w-3" />
                    Releases
                  </span>
                )}
                {group.enable_release_cycles && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
                    <GitBranch className="h-3 w-3" />
                    Cycles
                  </span>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
                Created {formatDate(group.created_at)}
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
